import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ECGHeart from "../../icons/ECGHeart";
import Spacer from "../../Spacer";
import { SignalChart } from "../../charts/SignalChart";
import PoincareChart from "../../charts/PoincareChart";
import PostExertionDecayChart from "../../charts/PostExertionDecayChart";
import FlowVolumeLoopsSection from "../FlowVolumeLoopsSection";
import AveragedFlowLoopsSection from "../AveragedFlowLoopsSection";
import { useUserWebAppPresentationFeatures } from "../../../hooks/useUserWebAppPresentationFeatures";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../../utils/results/recordingIdTime";
import MetricValue from "../MetricValue";
import LungIcon from "../../icons/LungIcon";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import { trackEvent } from "../../../utils/analytics/track";
import { pickRespiratoryObservedRrSeries } from "../../../utils/results/respiratoryPoincare";
import ResultViewScaffold from "../ResultViewScaffold";
import ResultCard from "../../common/ResultCard";
import RecordingQualitySection from "../RecordingQualitySection";
import {
  buildCardiopulmonaryAdditionalMetricsRecord,
  buildPoincareFromIntervals,
  pickCardiopulmonaryDetrendedSignal,
  pickHeartDxPeakIntervals,
} from "../../../utils/results/cardiopulmonaryView";
import { buildAdditionalRespiratoryMetricsFields } from "../../../utils/results/additionalRespiratoryMetrics";
import {
  hasFirestoreFlowLoops,
  pickAveragedFlowLoop,
  pickPrimaryFlowLoopRecord,
} from "../../../utils/results/flowLoops";
import { getByPath } from "../../../utils/results/metricsKVTableHelpers";
import {
  RECORDING_QUALITY_CHECK_STATES,
  RECORDING_QUALITY_OVERALL_STATES,
  buildRecordingQualityCheck,
  getRecordingQualitySummaryCopy,
  summarizeRecordingQualityChecks,
} from "../../../utils/results/recordingQuality";
import { pickHrSignalQuality, pickRr, pickRrSignalQuality } from "../../../utils/results/signalQuality";
import {
  pickHeartDxHrBpm,
  pickHeartDxHRV,
} from "../../../utils/results/heartdx";
import { isCardiopulmonaryCompleteDoc } from "../../../utils/results/cardiopulmonaryStatus";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../../utils/results/resultModePresentation";

function CardiopulmonaryResultViewImpl({
  data,
  docId,
  variant,
  repeatLinkTo = "",
  footer = null,
}) {
  const { t, i18n } = useTranslation();
  const isPulseResult =
    variant === "RecordingPulseScan" ||
    String(data?.appMode || "")
      .trim()
      .toLowerCase() === "pulse";
  const {
    showFullResults,
    resultFeatures = {},
    elementFeatures = {},
  } = useUserWebAppPresentationFeatures();
  const showCardiacQuality = Boolean(resultFeatures.cardiacQuality);
  const showSignalQuality =
    !isPulseResult && Boolean(resultFeatures.signalQuality);
  const showHr = Boolean(resultFeatures.hr);
  const showHrv = Boolean(resultFeatures.hrv);
  const showRr = !isPulseResult && Boolean(resultFeatures.rr);
  const showFlowLoops = !isPulseResult && Boolean(resultFeatures.flowLoops);
  const showPoincareChart = Boolean(elementFeatures.poincareChart);
  const showRespiratorySignalChart = !isPulseResult && Boolean(
    elementFeatures.respiratorySignalChart,
  );
  const showCardiacSummary = showHr || showHrv;
  const rr = useMemo(() => pickRr(data), [data]);
  const rrSignalQuality = useMemo(() => pickRrSignalQuality(data), [data]);
  const hr = useMemo(() => pickHeartDxHrBpm(data), [data]);
  const hrv = useMemo(() => pickHeartDxHRV(data), [data]);
  const hrQuality = useMemo(() => pickHrSignalQuality(data), [data]);
  const recordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(docId),
    [docId],
  );
  const recordingQualitySummary = useMemo(
    () =>
      summarizeRecordingQualityChecks([
        showCardiacQuality
          ? buildRecordingQualityCheck({
              key: "cardiac",
              label: t(
                "result.cardiopulmonary.cardiacQuality",
                "Cardiac Quality",
              ),
              rawStatus: hrQuality,
              recordingTimestamp,
              message:
                hrQuality != null
                  ? t(
                      "result.pulse.qualityFailTitle",
                      "Low heart-rate signal quality.",
                    )
                  : null,
            })
          : null,
        showSignalQuality
          ? buildRecordingQualityCheck({
              key: "respiratory",
              label: t(
                "result.cardiopulmonary.respiratoryQuality",
                "Respiratory Quality",
              ),
              rawStatus: rrSignalQuality,
              recordingTimestamp,
              message:
                rrSignalQuality != null
                  ? t(
                      "result.resp.qualityFailTitle",
                      "Low respiratory signal quality.",
                    )
                  : null,
            })
          : null,
      ]),
    [
      hrQuality,
      recordingTimestamp,
      rrSignalQuality,
      showCardiacQuality,
      showSignalQuality,
      t,
    ],
  );
  const cardiacQualityStatus =
    recordingQualitySummary.checks.find((check) => check.key === "cardiac")
      ?.state || RECORDING_QUALITY_CHECK_STATES.UNAVAILABLE;
  const respiratoryQualityStatus =
    recordingQualitySummary.checks.find((check) => check.key === "respiratory")
      ?.state || RECORDING_QUALITY_CHECK_STATES.UNAVAILABLE;
  const cardiacQualityIsFailing =
    cardiacQualityStatus === RECORDING_QUALITY_CHECK_STATES.FAIL;
  const respiratoryQualityIsFailing =
    respiratoryQualityStatus === RECORDING_QUALITY_CHECK_STATES.FAIL;

  // Grey the metric rows unless the quality is a clear pass. `hrMsg`/`rrMsg` only
  // cover an explicit "fail", so an unresolved quality (null -> "--") would
  // otherwise leave a "--" metric rendered in primary. Matches the disabled
  // logic on the quality rows themselves (status !== "pass").
  const cardiacMetricsDisabled =
    cardiacQualityStatus !== RECORDING_QUALITY_CHECK_STATES.PASS;
  const respiratoryMetricsDisabled =
    respiratoryQualityStatus !== RECORDING_QUALITY_CHECK_STATES.PASS;

  const peakIntervals = useMemo(() => pickHeartDxPeakIntervals(data), [data]);

  const poincare = useMemo(
    () => buildPoincareFromIntervals(peakIntervals),
    [peakIntervals],
  );

  const mpmDetrendedSignal = useMemo(
    () => pickCardiopulmonaryDetrendedSignal(data),
    [data],
  );
  const respiratorySignalLogContext = useMemo(
    () => ({
      docId,
      variant,
      appMode: data?.appMode || data?.captureMode || null,
    }),
    [data?.appMode, data?.captureMode, docId, variant],
  );

  const respiratoryObservedRrSeries = useMemo(
    () => pickRespiratoryObservedRrSeries(data),
    [data],
  );
  const additionalMetricsRecord = useMemo(
    () => buildCardiopulmonaryAdditionalMetricsRecord(data),
    [data],
  );
  const additionalMetricsFields = useMemo(
    () => buildAdditionalRespiratoryMetricsFields(t),
    [t],
  );
  const flowLoopRecord = useMemo(() => pickPrimaryFlowLoopRecord(data), [data]);
  const averagedFlowLoop = useMemo(() => pickAveragedFlowLoop(data), [data]);

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );
  const compactRecordingQualityStatus = useMemo(() => {
    if (!showCardiacQuality && !showSignalQuality) {
      return null;
    }

    if (
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PASSED &&
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PENDING
    ) {
      return null;
    }

    const summaryCopy = getRecordingQualitySummaryCopy(t, recordingQualitySummary);
    return summaryCopy
      ? {
          tone: summaryCopy.tone,
          text: summaryCopy.title,
          accessibleLabel: summaryCopy.accessibleLabel,
        }
      : null;
  }, [
    recordingQualitySummary,
    showCardiacQuality,
    showSignalQuality,
    t,
  ]);
  const showRecordingQualitySection =
    (showCardiacQuality || showSignalQuality) &&
    compactRecordingQualityStatus == null;

  const showDetailPanels =
    showPoincareChart ||
    showRespiratorySignalChart ||
    showFlowLoops ||
    (!isPulseResult && showFullResults && respiratoryObservedRrSeries.length);
  const details = showDetailPanels ? (
    <section className="w-full p-4 pt-2 flex flex-col gap-4">
      {showRespiratorySignalChart ? (
        <ResultCard
          icon={<LungIcon />}
          title={t("result.resp.signalChartTitle", "Respiratory Signal")}
        >
          {mpmDetrendedSignal.points.length ? (
            <SignalChart
              smoothSignal={mpmDetrendedSignal.points}
              rawSignal={[]}
              peaks={[]}
              troughs={[]}
              height={220}
              maxSecs={mpmDetrendedSignal.maxSecs}
              logContext={respiratorySignalLogContext}
            />
          ) : (
            <div className="muted-text">
              {t("result.resp.noSignal", "No signal trace available.")}
            </div>
          )}
        </ResultCard>
      ) : null}

      {!isPulseResult && showFullResults && respiratoryObservedRrSeries.length ? (
        <ResultCard
          icon={<LungIcon />}
          title={t(
            "result.resp.rrPerBreathHeading",
            "Respiratory Rate per Breath",
          )}
        >
          <PostExertionDecayChart
            points={respiratoryObservedRrSeries}
            height={260}
            showFits={false}
          />
        </ResultCard>
      ) : null}

      {showPoincareChart ? (
        <ResultCard
          icon={<ECGHeart />}
          title={t("result.pulse.poincare", "Poincaré")}
        >
          {poincare.x.length && poincare.y.length ? (
            <PoincareChart
              x={poincare.x}
              y={poincare.y}
              height={280}
              initialMode="basic"
            />
          ) : (
            <div className="muted-text text-center">
              {t("result.pulse.noPoincare", "No Poincaré data available.")}
            </div>
          )}
        </ResultCard>
      ) : null}

      {showFlowLoops ? (
        <>
          {averagedFlowLoop ? (
            <ResultCard
              icon={<LungIcon />}
              title={t(
                averagedFlowLoop?.mode === "windowed"
                  ? "results.flowLoops.averagedSectionTitlePlural"
                  : "results.flowLoops.averagedSectionTitle",
                averagedFlowLoop?.mode === "windowed"
                  ? "Average Flow-Volume Loops"
                  : "Average Flow-Volume Loop",
              )}
            >
              <AveragedFlowLoopsSection flowLoopSource={flowLoopRecord} />
            </ResultCard>
          ) : null}

          <ResultCard
            icon={<LungIcon />}
            title={t("results.flowLoops.sectionTitle", "Flow-Volume Loops")}
          >
            {hasFirestoreFlowLoops(flowLoopRecord) ? (
              <FlowVolumeLoopsSection
                showSubtitle={false}
                recordJson={flowLoopRecord}
                hideSpirometryManeuverBreaths={false}
                showOverlay={true}
                showMetricBar={false}
                showMetricsTable={false}
              />
            ) : (
              <div className="muted-text">
                {t(
                  "result.spiro.noFlowLoops",
                  "No additional flow-loop data available.",
                )}
              </div>
            )}
          </ResultCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {additionalMetricsFields.map((field) => {
              const value = field.fmt(
                getByPath(additionalMetricsRecord, field.path),
                additionalMetricsRecord,
              );

              return (
                <ResultCard
                  key={field.path}
                  icon={<LungIcon />}
                  title={t(field.i18nKey, { defaultValue: field.label })}
                >
                  <p className="title-text text-primary">
                    {value}
                    {field.unit && value !== "--" ? (
                      <span className="muted-text"> {field.unit}</span>
                    ) : null}
                  </p>
                </ResultCard>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  ) : null;

  return (
    <ResultViewScaffold
      title={getResultModeLabel(variant, t)}
      icon={getResultModeIcon(variant)}
      recordingTime={recordingTime}
      statusLine={compactRecordingQualityStatus}
      details={details}
      footer={footer}
    >
      <Spacer />

      {showRecordingQualitySection ? (
        <RecordingQualitySection
          summary={recordingQualitySummary}
          guidanceLinkTo="/guidance/cardiopulmonary"
          guidanceCtaLabel={t("result.cardiopulmonary.guidanceCta", "Tips")}
          repeatLinkTo={repeatLinkTo}
          repeatCtaLabel={t(
            "result.recordingQuality.repeatCta",
            "Repeat",
          )}
          onGuidanceClick={() =>
            trackEvent("cardiopulmonary_result_click_guidance", {
              cardiac_quality_failed: cardiacQualityIsFailing,
              respiratory_quality_failed: respiratoryQualityIsFailing,
            })
          }
        />
      ) : null}

      {showCardiacSummary ? (
        <ResultSummarySection
          icon={<ECGHeart />}
          title={t("result.pulse.cardiac", "Cardiac")}
          disabled={cardiacMetricsDisabled}
        >
          {showHr ? (
            <ResultSummaryRow
              label={t("result.pulse.hrLabel", "Heart Rate")}
              value={
                <MetricValue
                  metric="hr"
                  value={hr}
                  unit={t("result.pulse.unit", "bpm")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={cardiacMetricsDisabled}
            />
          ) : null}
          {showHrv ? (
            <ResultSummaryRow
              label={t("result.pulse.hrvLabel", "HRV")}
              value={
                <MetricValue
                  metric="hrv"
                  value={hrv}
                  unit={t("result.pulse.hrvUnit", "ms")}
                  recordingTimestamp={recordingTimestamp}
                />
              }
              disabled={cardiacMetricsDisabled}
            />
          ) : null}
        </ResultSummarySection>
      ) : null}

      {showRr ? (
        <ResultSummarySection
          icon={<LungIcon />}
          title={t("result.tidal.respiratory", "Respiratory")}
          disabled={respiratoryMetricsDisabled}
        >
          <ResultSummaryRow
            label={t("result.resp.rrLongLabel", "Respiratory Rate")}
            value={
              <MetricValue
                metric="rr"
                value={rr}
                unit={t("result.resp.rrUnit", "bpm")}
                recordingTimestamp={recordingTimestamp}
              />
            }
            disabled={respiratoryMetricsDisabled}
          />
        </ResultSummarySection>
      ) : null}
    </ResultViewScaffold>
  );
}

const CardiopulmonaryResultView = memo(CardiopulmonaryResultViewImpl);

CardiopulmonaryResultView.isReady = (data) => {
  return (
    isCardiopulmonaryCompleteDoc(data) &&
    (pickRr(data) != null || pickHeartDxHrBpm(data) != null)
  );
};

export default CardiopulmonaryResultView;
